import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  buildRequirementsSummary,
  createDefaultFacet,
  FACET_TYPE_LABELS,
  getValueFieldDisplay,
  IDSFacet,
  IDSSpecification,
  IDSValueField,
  IFC_VERSIONS,
  NECESSITY_OPTIONS
} from '../models/ids.model';
import {matchEntityGroup} from '../models/ifc-entity-groups';
import {IdsFacetComponent} from '../ids-facet/ids-facet.component';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faAnglesDown, faAnglesUp, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicSelectComponent
} from '@buildinginformationcloud/bic-ui-components';
import {SpecDetailTab} from '../ids-editor/ids-editor.component';
import {ChangeMarkerComponent} from '../../../../../util/design/change-marker/change-marker.component';
import {MyCheckFieldChange} from '../../../../../ruleManagement/proposal/mycheck-diff';
import {
  facetChangesOf,
  facetFieldPath,
  IdsFacetBlock,
  RemovedIdsFacet
} from '../../../../../ruleManagement/proposal/ids-mycheck-diff';
import {RemovedRuleCardComponent} from '../../../../../util/design/removed-rule-card/removed-rule-card.component';

@Component({
  selector: 'app-ids-specification',
  templateUrl: './ids-specification.component.html',
  imports: [IdsFacetComponent, FormsModule, FaIconComponent, BicInputComponent, BicButtonComponent, BicSelectComponent, BicDropdownElementComponent, ChangeMarkerComponent, RemovedRuleCardComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class IdsSpecificationComponent {
  @Input() specification!: IDSSpecification;
  @Input() activeTab: SpecDetailTab = 'general';
  @Input() submitted = false;
  /**
   * Änderungen eines chatBIC-Vorschlags an dieser Spezifikation, Pfad → Vorher/Nachher (Pfadschema
   * siehe `IdsSpecChanges.changed` in `ids-mycheck-diff.ts`).
   */
  @Input() fieldChanges?: Map<string, MyCheckFieldChange>;
  /** Präfix der Review-Schlüssel (`spec:<diffKey>`) für `data-proposal-review` der Einzelfelder: Der
   *  Stepper der Review-Leiste im Edit-Chat scrollt über `<prefix>:<path>` zum Feld. */
  @Input() reviewKeyPrefix = '';
  /** Übernehmen/Verwerfen eines einzelnen Felds, mit seinem Pfad. */
  @Output() fieldAccepted = new EventEmitter<string>();
  @Output() fieldRejected = new EventEmitter<string>();
  /** Übernehmen/Verwerfen einer ganzen Facette (Anforderung bzw. Anwendbarkeit), mit Block und Position. */
  @Output() facetAccepted = new EventEmitter<{ block: IdsFacetBlock, index: number }>();
  @Output() facetRejected = new EventEmitter<{ block: IdsFacetBlock, index: number }>();
  /**
   * Facetten, die der Vorschlag aus dieser Spezifikation entfernt (`IdsSpecChanges.removedFacets`). Sie
   * stehen nicht mehr in `specification` und werden als eigene Karte unter dem Block gezeigt.
   */
  @Input() removedFacets?: RemovedIdsFacet[];
  /** ✓ an einer entfernten Facette (Entfernen bestätigen) bzw. ✗ (wiederherstellen). */
  @Output() removedFacetAccepted = new EventEmitter<RemovedIdsFacet>();
  @Output() removedFacetRejected = new EventEmitter<RemovedIdsFacet>();
  @Output() specificationChange = new EventEmitter<IDSSpecification>();

  /** Bezeichnung einer entfernten Facette, z. B. „Entität: IfcSlab" oder „Eigenschaft: Pset_WallCommon.FireRating". */
  removedFacetLabel(facet: IDSFacet): string {
    const type = FACET_TYPE_LABELS[facet.type];
    switch (facet.type) {
      case 'entity': return `${type}: ${this.vfd(facet.entityName)}`;
      case 'entityGroup': return `${type}: ${this.entityGroupLabel(facet)}`;
      case 'attribute': return `${type}: ${this.vfd(facet.attributeName)}`;
      case 'property': return `${type}: ${this.vfd(facet.propertySet)}.${this.vfd(facet.propertyName)}`;
      case 'classification': return `${type}: ${this.vfd(facet.classificationSystem)}`;
      case 'material': return `${type}: ${this.vfd(facet.materialName)}`;
      case 'partOf': return `${type}: ${this.vfd(facet.partOfEntityName)}`;
    }
  }

  /** Die Änderungen genau einer Facette, für `app-ids-facet`. */
  facetChanges(block: IdsFacetBlock, index: number): Map<string, MyCheckFieldChange> | undefined {
    return facetChangesOf(this.fieldChanges, block, index);
  }

  onFacetChangeAccepted(block: IdsFacetBlock, index: number, field: string): void {
    this.fieldAccepted.emit(facetFieldPath(block, index, field));
  }

  onFacetChangeRejected(block: IdsFacetBlock, index: number, field: string): void {
    this.fieldRejected.emit(facetFieldPath(block, index, field));
  }

  protected readonly faPlus = faPlus;
  protected readonly faAnglesDown = faAnglesDown;
  protected readonly faAnglesUp = faAnglesUp;
  protected readonly NECESSITY_OPTIONS = NECESSITY_OPTIONS;
  protected readonly IFC_VERSIONS = IFC_VERSIONS;

  get allApplicabilityExpanded(): boolean {
    return this.specification.applicability.length > 0 && this.specification.applicability.every(f => f.expanded);
  }

  get allRequirementsExpanded(): boolean {
    return this.specification.requirements.length > 0 && this.specification.requirements.every(f => f.expanded);
  }

  toggleAllApplicability() {
    const expand = !this.allApplicabilityExpanded;
    this.specification.applicability.forEach(f => f.expanded = expand);
    this.emitChange();
  }

  toggleAllRequirements() {
    const expand = !this.allRequirementsExpanded;
    this.specification.requirements.forEach(f => f.expanded = expand);
    this.emitChange();
  }

  /** Template helper – delegates to the pure function */
  vfd(field: IDSValueField, fallback = '...'): string {
    return getValueFieldDisplay(field, fallback);
  }

  /** Anzeigename der Entitätengruppe einer Facette, für die Freitext-Zusammenfassung. */
  entityGroupLabel(facet: IDSFacet): string {
    const group = matchEntityGroup(facet.entityName.enumValues ?? [], this.specification.ifcVersions);
    return group?.label ?? this.vfd(facet.entityName);
  }

  /** Natürlichsprachlicher Hinweistext für die Anforderungen, gruppiert nach Anwendbarkeit (Erforderlich/Optional/Verboten). */
  get requirementsSummary(): string {
    return buildRequirementsSummary(this.specification.requirements);
  }

  addApplicabilityFacet() {
    this.specification.applicability.push(createDefaultFacet('entity'));
    this.emitChange();
  }

  addRequirementFacet() {
    const facet = createDefaultFacet('property');
    facet.metaNecessity = 'required';
    this.specification.requirements.push(facet);
    this.emitChange();
  }

  removeApplicabilityFacet(index: number) {
    this.specification.applicability.splice(index, 1);
    this.emitChange();
  }

  duplicateApplicabilityFacet(index: number) {
    const original = this.specification.applicability[index];
    const copy: IDSFacet = JSON.parse(JSON.stringify(original));
    copy.id = crypto.randomUUID();
    this.specification.applicability.splice(index + 1, 0, copy);
    this.emitChange();
  }

  removeRequirementFacet(index: number) {
    this.specification.requirements.splice(index, 1);
    this.emitChange();
  }

  duplicateRequirementFacet(index: number) {
    const original = this.specification.requirements[index];
    const copy: IDSFacet = JSON.parse(JSON.stringify(original));
    copy.id = crypto.randomUUID();
    this.specification.requirements.splice(index + 1, 0, copy);
    this.emitChange();
  }

  onApplicabilityFacetChange(index: number, facet: IDSFacet) {
    this.specification.applicability[index] = facet;
    this.emitChange();
  }

  onRequirementFacetChange(index: number, facet: IDSFacet) {
    this.specification.requirements[index] = facet;
    this.emitChange();
  }

  toggleIfcVersion(version: string) {
    const index = this.specification.ifcVersions.indexOf(version);
    if (index >= 0) {
      this.specification.ifcVersions.splice(index, 1);
    } else {
      this.specification.ifcVersions.push(version);
    }
    this.emitChange();
  }

  emitChange() {
    this.specificationChange.emit({...this.specification});
  }
}



